import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocketContext } from '../contexts/WebSocketContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use native XMLHttpRequest to bypass rrweb-recorder fetch wrapper
const nativeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: true, data, status: xhr.status });
        } catch (e) {
          resolve({ ok: true, data: xhr.responseText, status: xhr.status });
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

const GlobalStatsBar = () => {
  const navigate = useNavigate();
  const { refreshTriggers } = useWebSocketContext();
  const [pipelineStats, setPipelineStats] = useState(null);
  const [unattendedStats, setUnattendedStats] = useState({
    unattended_count: 0,
    unattended_lead_ids: []
  });
  const [cancelledCount, setCancelledCount] = useState(0);
  const [openTasksCount, setOpenTasksCount] = useState(0);

  useEffect(() => {
    fetchPipelineStats();
    fetchUnattendedStats();
    fetchCancelledCount();
    fetchOpenTasksCount();
  }, [refreshTriggers.leads]);

  const fetchPipelineStats = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/leads/stats/pipeline`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (result.ok) {
        setPipelineStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching pipeline stats:', error);
    }
  };

  const fetchUnattendedStats = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/leads/stats/unattended-new-leads`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (result.ok) {
        setUnattendedStats({
          unattended_count: result.data.unattended_count || 0,
          unattended_lead_ids: result.data.unattended_lead_ids || []
        });
      }
    } catch (error) {
      console.error('Error fetching unattended stats:', error);
    }
  };

  const fetchCancelledCount = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/leads/stats/stage-counts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (result.ok && result.data) {
        setCancelledCount(result.data.cancelled || 0);
      }
    } catch (error) {
      console.error('Error fetching cancelled count:', error);
    }
  };

  const fetchOpenTasksCount = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/admin/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (result.ok && result.data) {
        // Count pending, in_progress, and overdue tasks
        const openTasks = result.data.filter(t => 
          ['pending', 'in_progress', 'overdue'].includes(t.status)
        );
        setOpenTasksCount(openTasks.length || 0);
      }
    } catch (error) {
      console.error('Error fetching open tasks count:', error);
    }
  };

  const handleLeadsToWorkClick = () => {
    navigate('/admin/leads?filter=to-work');
  };

  const handleCancelledClick = () => {
    navigate('/admin/leads?filter=cancelled');
  };

  const handleUnattendedClick = () => {
    navigate('/admin/leads?filter=unattended');
  };

  const handleOpenTasksClick = () => {
    navigate('/admin/tasks');
  };

  if (!pipelineStats) {
    return null;
  }

  return (
    <>
      {/* Leads To Work Chip */}
      <div
        onClick={handleLeadsToWorkClick}
        className="stat-chip"
        style={{
          cursor: 'pointer',
          padding: '8px 16px',
          background: '#6b5b95',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          color: 'white'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#5a4a85';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#6b5b95';
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: '600' }}>Leads To Work:</span>
        <span style={{ fontSize: '15px', fontWeight: '700' }}>{(pipelineStats.total_leads || 0) - cancelledCount}</span>
      </div>

      {/* Cancelled Leads Chip */}
      <div
        onClick={handleCancelledClick}
        className="stat-chip"
        style={{
          cursor: 'pointer',
          padding: '8px 16px',
          background: '#fb923c',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          color: 'white'
        }}
        title="Cancelled leads"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#f97316';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#fb923c';
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: '600' }}>Cancelled Leads:</span>
        <span style={{ fontSize: '15px', fontWeight: '700' }}>{cancelledCount}</span>
      </div>

      {/* Zero Updates Chip */}
      <div
        onClick={handleUnattendedClick}
        className="stat-chip"
        style={{
          cursor: 'pointer',
          padding: '8px 16px',
          background: unattendedStats.unattended_count > 0 ? '#dc2626' : '#6b7280',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          color: 'white'
        }}
        title="New leads (stage='new') with NO manual updates today (resets daily at midnight IST)"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = unattendedStats.unattended_count > 0 ? '#b91c1c' : '#4b5563';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = unattendedStats.unattended_count > 0 ? '#dc2626' : '#6b7280';
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: '600' }}>⚠️ New Zero Updates:</span>
        <span style={{ fontSize: '15px', fontWeight: '700' }}>{unattendedStats.unattended_count}</span>
      </div>

      {/* Open Tasks Chip */}
      <div
        onClick={handleOpenTasksClick}
        className="stat-chip"
        style={{
          cursor: 'pointer',
          padding: '8px 16px',
          background: openTasksCount > 0 ? '#14b8a6' : '#6b7280',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          color: 'white'
        }}
        title="Open tasks (pending, in progress, or overdue)"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = openTasksCount > 0 ? '#0f9488' : '#4b5563';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = openTasksCount > 0 ? '#14b8a6' : '#6b7280';
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: '600' }}>✓ Open Tasks:</span>
        <span style={{ fontSize: '15px', fontWeight: '700' }}>{openTasksCount}</span>
      </div>
    </>
  );
};

export default GlobalStatsBar;
